import React, { useState } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { CustomDatePicker } from "../../components/UI/CustomDatePicker";
import useGetClinics from "../../hooks/useGetClinics";
import useGetPractitioners from "../../hooks/useGetPractitioners";
import useGetTreatmentTypesOld from "../../hooks/useGetTreatmentTypesOld";
import useCreateAppointmentMutation from "../../hooks/useCreateAppointmentMutation";

const CreateAppointment = () => {
  const { _id_ } = useParams();
  const navigate = useNavigate();

  const { setActive, setBreadcrumbTitle, isTrialLocked } = useOutletContext();
  setActive("appointments");
  setBreadcrumbTitle("Appointment Create");

  const [showAddButton, setShowAddButton] = useState(true);
  const [dateTime, changeDateTime] = useState("");
  const [clinicId, setClinicId] = useState(null);

  const { data: ClinicList } = useGetClinics();
  const { data: PractitionerList } = useGetPractitioners({ clinicId });
  const { data: treatmentTypes = [] } = useGetTreatmentTypesOld();
  const createAppointmentMutation = useCreateAppointmentMutation();

  const [Appointment, setAppointmentData] = useState({
    patient: _id_,
    duration: 30,
    notes: "",
    status: "",
    practitioner: "",
    clinic: "",
    notifypush: false,
    notifysms: false,
    notifyemail: false,
    type: "2",
    appointment_type_list: [{ app_type: 2 }],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAppointmentData((Appointment) => ({
      ...Appointment,
      [name]: value,
    }));
    if (name === "clinic") setClinicId(value);
  };

  const CreateInstance = (e) => {
    e.preventDefault();
    createAppointmentMutation.mutate(
      { data: { ...Appointment, time: dateTime ? dateTime.split(".")[0] : "" } },
      {
        onSuccess: () => {
          navigate(`/patient/${_id_}/appointment/`);
        },
      }
    );
  };

  const handleList = (e, rowIndex) => {
    const { value } = e.target;
    setAppointmentData((Appointment) => ({
      ...Appointment,
      appointment_type_list: [
        ...Appointment.appointment_type_list.map((data, index) => {
          if (index === rowIndex) {
            return { app_type: value };
          } else {
            return data;
          }
        }),
      ],
    }));
  };

  const handleDelete = (id) => {
    const data = Appointment.appointment_type_list.filter((item, index) => index !== id);
    setAppointmentData({
      ...Appointment,
      appointment_type_list: data,
    });
  };

  const AppointmentDataList = () => {
    setShowAddButton(true);
    setAppointmentData({
      ...Appointment,
      appointment_type_list: [...Appointment.appointment_type_list, { app_type: "" }],
    });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Create Appointment
      </Typography>

      <form onSubmit={CreateInstance}>
        {/* Schedule section */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Schedule
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <CustomDatePicker
            label="Appointment Time"
            value={dateTime}
            onChange={changeDateTime}
            required
            variant="outlined"
            minutesStep={15}
          />
          <TextField
            label="Duration (minutes)"
            type="number"
            size="small"
            required
            slotProps={{
              htmlInput: { step: 15, min: 15, max: 60 },
            }}
            value={Appointment.duration}
            name="duration"
            onChange={handleChange}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Assignment section */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Assignment
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          <FormControl size="small" required>
            <InputLabel>Clinic</InputLabel>
            <Select label="Clinic" value={Appointment.clinic} name="clinic" onChange={handleChange}>
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {Array.isArray(ClinicList) &&
                ClinicList.map((data) => (
                  <MenuItem value={data.id} key={data.id}>
                    {data.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl size="small" required>
            <InputLabel>Practitioner</InputLabel>
            <Select
              label="Practitioner"
              value={Appointment.practitioner}
              name="practitioner"
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {PractitionerList &&
                PractitionerList.map((data) =>
                  data.practitioner_clinic[0]?.id !== "" ? (
                    <MenuItem value={data.id} key={data.id}>
                      {data.name}
                    </MenuItem>
                  ) : null
                )}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={Appointment.status} name="status" onChange={handleChange}>
              <MenuItem value="UPCOMING">UPCOMING</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <TextField
          label="Notes"
          fullWidth
          size="small"
          multiline
          rows={2}
          value={Appointment.notes}
          name="notes"
          onChange={handleChange}
          placeholder="Enter notes here..."
        />

        <Divider sx={{ my: 3 }} />

        {/* Appointment Types */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Appointment Types
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={AppointmentDataList}
          >
            Add Type
          </Button>
        </Box>

        <Stack spacing={1}>
          {Appointment.appointment_type_list.map((AppointmentType, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {showAddButton && (
                <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                  <InputLabel>Appointment Type</InputLabel>
                  <Select
                    label="Appointment Type"
                    name="app_type"
                    value={AppointmentType.app_type || ""}
                    onChange={(e) => handleList(e, index)}
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {treatmentTypes.map((data) => (
                      <MenuItem value={data.id} key={data.id}>
                        {data.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Notifications */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Notifications
        </Typography>
        <Stack direction="row" spacing={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Appointment.notifyemail}
                onChange={() =>
                  setAppointmentData((prev) => ({
                    ...prev,
                    notifyemail: !prev.notifyemail,
                  }))
                }
              />
            }
            label="Email"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Appointment.notifypush}
                onChange={() =>
                  setAppointmentData((prev) => ({
                    ...prev,
                    notifypush: !prev.notifypush,
                  }))
                }
              />
            }
            label="Push"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Appointment.notifysms}
                onChange={() =>
                  setAppointmentData((prev) => ({
                    ...prev,
                    notifysms: !prev.notifysms,
                  }))
                }
              />
            }
            label="SMS"
          />
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button variant="outlined" component={Link} to={`/patient/${_id_}/appointment/`}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={isTrialLocked}>
            Create Appointment
          </Button>
        </Stack>
      </form>
    </Paper>
  );
};

export default CreateAppointment;
